// src/sectors/pilates/sessions/components/calendar/MobileDayList/MobileDayList.jsx
import React from 'react';
import Divider from '@mui/material/Divider';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';

import { buildDaySessions } from './mobileDayList.utils';

import DayHeader from './parts/DayHeader';
import EmptyDayCard from './parts/EmptyDayCard';
import SessionActionSheet from './parts/SessionActionSheet';
import SlotPickerDialog from './parts/SlotPickerDialog';
import { SoftSessionCard } from '../../SoftSessionCard';
import { DAY_LABELS } from 'sectors/pilates/sessions/data/mockCalendarData';
import { SoftShell } from './mobileDayList.atoms';

export function MobileDayList({
  days,
  slots,
  today,
  trainersById,
  byDaySlot,
  onSessionClick,

  editMode = false,
  copyMode = false,
  attendanceMode = false,

  onSessionEdit,
  onSessionDelete,

  onMoveRequest,
  onPickCreateSlot,
  onCopyStart,

  onEmptyDayAdd,
}) {
  const theme = useMuiTheme();
  function sortSessionsByTime(list = []) {
    return [...(list || [])].sort((a, b) => {
      const aStart = Number(a?.startMin ?? a?.start ?? 0);
      const bStart = Number(b?.startMin ?? b?.start ?? 0);
      if (aStart !== bStart) return aStart - bStart;

      // aynı saatteyse: duration/end ile stabil sırala
      const aEnd = Number(a?.endMin ?? aStart + Number(a?.durationMin ?? 0));
      const bEnd = Number(b?.endMin ?? bStart + Number(b?.durationMin ?? 0));
      if (aEnd !== bEnd) return aEnd - bEnd;

      // en son id ile stabil (string compare)
      return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
    });
  }

  // ----- accordion: only 1 day open -----
  const [openDay, setOpenDay] = React.useState(() => {
    const arr = days || [];
    if (today && arr.includes(today)) return today;
    return arr[0] || '';
  });

  // days/today değişince openDay’i stabilize et
  React.useEffect(() => {
    const arr = days || [];
    if (!arr.length) return;

    // openDay geçersizse düzelt
    if (!openDay || !arr.includes(openDay)) {
      if (today && arr.includes(today)) setOpenDay(today);
      else setOpenDay(arr[0]);
      return;
    }

    // today geldiyse ve openDay boşsa bugün aç
    if (!openDay && today && arr.includes(today)) setOpenDay(today);
  }, [days?.length, today]);

  const toggleDay = (day) => {
    setOpenDay((cur) => (cur === day ? '' : day));
  };

  // ----- action sheet state -----
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [sheetSession, setSheetSession] = React.useState(null);

  // ----- slot picker state -----
  const [pickOpen, setPickOpen] = React.useState(false);
  const [pickKind, setPickKind] = React.useState(null); // "MOVE" | "COPY" | "CREATE"
  const [pickDay, setPickDay] = React.useState('');
  const [pickStartMin, setPickStartMin] = React.useState(null);

  const [busy, setBusy] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState('');

  const openSheetFor = (s) => {
    setSheetSession(s || null);
    setSheetOpen(true);
  };

  const closeSheet = () => {
    if (busy) return;
    setSheetOpen(false);
    setSheetSession(null);
  };

  const openPick = ({ kind, dayISO, startMin }) => {
    setErrMsg('');
    setPickKind(kind);
    setPickDay(dayISO || '');
    setPickStartMin(Number.isFinite(Number(startMin)) ? Number(startMin) : null);
    setPickOpen(true);
  };

  const closePick = () => {
    if (busy) return;
    setPickOpen(false);
    setPickKind(null);
    setPickDay('');
    setPickStartMin(null);
    setErrMsg('');
  };

  const confirmPick = async () => {
    if (!pickDay || pickStartMin == null) return;
    if (busy) return;

    setErrMsg('');

    const target = {
      dayISO: String(pickDay).slice(0, 10),
      startMin: Number(pickStartMin) || 0,
    };

    try {
      setBusy(true);

      if (pickKind === 'CREATE') {
        await Promise.resolve(onPickCreateSlot?.(target));
        closePick();
        closeSheet();
        return;
      }

      const s = sheetSession;
      if (!s?.id) {
        setErrMsg('Seans bulunamadı (sheetSession yok).');
        return;
      }

      const payload = {
        sessionId: String(s.id),
        from: {
          dayISO: String(s.dayISO).slice(0, 10),
          startMin: Number(s.startMin) || 0,
        },
        to: target,
      };

      if (pickKind === 'MOVE') {
        if (!onMoveRequest) {
          setErrMsg('onMoveRequest bağlı değil (parent prop yok).');
          return;
        }
        await Promise.resolve(onMoveRequest(payload));
        closePick();
        closeSheet();
        return;
      }

      if (pickKind === 'COPY') {
        if (!onCopyStart) {
          setErrMsg('onCopyStart bağlı değil (parent prop yok).');
          return;
        }
        await Promise.resolve(onCopyStart(payload));
        closePick();
        closeSheet();
        return;
      }

      setErrMsg('İşlem tipi bulunamadı.');
    } catch (e) {
      console.error('[MobileDayList] PICK FAILED:', e);
      setErrMsg(e?.message || 'İşlem sırasında hata oluştu.');
    } finally {
      setBusy(false);
    }
  };

  // ----- auto scroll to today (only when today is open) -----
  const listRef = React.useRef(null);
  const todayRef = React.useRef(null);
  const didAutoScrollRef = React.useRef(false);

  React.useEffect(() => {
    // sadece today açıkken scroll
    if (didAutoScrollRef.current) return;
    if (!listRef.current) return;
    if (!today) return;
    if (openDay !== today) return;

    const arr = days || [];
    if (!arr.includes(today)) return;

    const doScroll = () => {
      const container = listRef.current;
      const node = todayRef.current;
      if (!container || !node) return false;

      const containerRect = container.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      const nodeTopInside = nodeRect.top - containerRect.top + container.scrollTop;

      const TOP_BAR_PX = 72;
      const EXTRA_GAP_PX = 12;

      const targetTop = Math.max(0, nodeTopInside - TOP_BAR_PX - EXTRA_GAP_PX);
      container.scrollTo({ top: targetTop, behavior: 'smooth' });
      return true;
    };

    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        const ok = doScroll();
        if (ok) {
          didAutoScrollRef.current = true;
          return;
        }

        const t = setTimeout(() => {
          doScroll();
          didAutoScrollRef.current = true;
        }, 120);

        return () => clearTimeout(t);
      });

      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [today, openDay, days?.length]);

  return (
    <>
      <MDBox
        sx={{
          pt: 'env(safe-area-inset-top)',
          pb: 'env(safe-area-inset-bottom)',
          pl: 'env(safe-area-inset-left)',
          pr: 'env(safe-area-inset-right)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <MDBox
          ref={listRef}
          sx={{
            flex: 1,
            p: 1.25,
            display: 'grid',
            gap: 1.0, // biraz sıkı
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            minHeight: 0,
          }}
        >
          {(days || []).map((day, i) => {
            const isToday = day === today;
            const isOpen = openDay === day;

            // kapalıyken hesap yükünü azaltmak için istersen count'u byDaySlot'tan çekebiliriz.
            // şimdilik aynı kalsın:
            // ✅ count her zaman doğru
            const allDaySessionsRaw = buildDaySessions({ day, slots, byDaySlot });
            const allDaySessions = sortSessionsByTime(allDaySessionsRaw);

            const count = allDaySessions.length;
            const daySessions = isOpen ? allDaySessions : [];

            return (
              <SoftShell
                key={day}
                ref={isToday && isOpen ? todayRef : undefined}
                tone="blue"
                sx={(t) => ({
                  border: isToday
                    ? `1px solid ${alpha(t.palette.info.main, 0.35)}`
                    : `1px solid ${alpha(
                        t.palette.text.primary,
                        t.palette.mode === 'dark' ? 0.12 : 0.1
                      )}`,
                  boxShadow: isToday
                    ? `0 14px 34px ${alpha(t.palette.info.main, 0.09)}`
                    : undefined,
                })}
              >
                <MDBox
                  onClick={() => toggleDay(day)}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <DayHeader
                    day={day}
                    dayLabel={DAY_LABELS?.[i] ?? `Gün ${i + 1}`}
                    isToday={isToday}
                    count={count}
                    editMode={editMode}
                    isOpen={isOpen} // ✅ ekle
                    onCreateClick={(e) => {
                      e?.stopPropagation?.();
                      if (!isOpen) setOpenDay(day);
                      openPick({ kind: 'CREATE', dayISO: day });
                    }}
                  />
                </MDBox>

                {/* kapalıysa içerik yok */}
                {isOpen ? (
                  <>
                    <Divider />

                    <MDBox sx={{ p: 1.0, display: 'grid', gap: 0.75 }}>
                      {daySessions.length === 0 ? (
                        <EmptyDayCard
                          day={day}
                          editMode={editMode}
                          onEmptyDayAdd={onEmptyDayAdd}
                          onCreate={() => openPick({ kind: 'CREATE', dayISO: day })}
                        />
                      ) : (
                        daySessions.map((s) => {
                          const tr = trainersById?.get?.(String(s.trainerId));
                          const trainerName =
                            (s?.trainerName && s.trainerName !== 'Unknown' ? s.trainerName : '') ||
                            tr?.name ||
                            '—';

                          return (
                            <SoftSessionCard
                              key={s.id}
                              // ✅ burada ileride variant="compact" geçeceğiz
                              s={{ ...s, trainerName }}
                              onClick={() => {
                                if (copyMode) return onSessionClick?.(s);
                                if (attendanceMode) return onSessionClick?.(s);
                                if (editMode) return openSheetFor(s);
                                onSessionClick?.(s);
                              }}
                            />
                          );
                        })
                      )}
                    </MDBox>
                  </>
                ) : (
                  // kapalı gün için minik alt padding (naif)
                  <MDBox sx={{ pb: 0.75 }} />
                )}
              </SoftShell>
            );
          })}
        </MDBox>
      </MDBox>

      <SessionActionSheet
        open={sheetOpen}
        onClose={closeSheet}
        session={sheetSession}
        busy={busy}
        onEdit={() => {
          closeSheet();
          onSessionEdit?.(sheetSession);
        }}
        onMovePick={() =>
          openPick({ kind: 'MOVE', dayISO: sheetSession?.dayISO, startMin: sheetSession?.startMin })
        }
        onCopyPick={() =>
          openPick({ kind: 'COPY', dayISO: sheetSession?.dayISO, startMin: sheetSession?.startMin })
        }
        onDelete={() => {
          closeSheet();
          onSessionDelete?.(sheetSession);
        }}
      />

      <SlotPickerDialog
        open={pickOpen}
        onClose={closePick}
        kind={pickKind}
        days={days}
        slots={slots}
        pickDay={pickDay}
        pickStartMin={pickStartMin}
        setPickDay={setPickDay}
        setPickStartMin={setPickStartMin}
        busy={busy}
        errMsg={errMsg}
        onConfirm={confirmPick}
      />
    </>
  );
}
